import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { configService } from "../services/configService";
import { getPartnerLabel, PartnerType } from "../utils/partners.enum";
import { getPartnerBgColor, getPartnerImage } from "../utils/partners";
import { ProductCatalogItem } from "../services/configService/getProductsCatalog";

export const useProductsCatalog = () => {
  const query = useQuery({
    queryKey: ["productsCatalog"],
    queryFn: configService.getProductsCatalog,
  });

  const catalog = query.data;

  const getProduct = useCallback(
    (key?: PartnerType | string | null): ProductCatalogItem | undefined => {
      if (!key) return undefined;
      return catalog?.find((product) => product.key === key);
    },
    [catalog],
  );

  const getProductLabel = useCallback(
    (key?: PartnerType | string | null): string => {
      if (!key) return "";
      const product = getProduct(key);
      return product?.label ?? getPartnerLabel(key as PartnerType);
    },
    [getProduct],
  );

  const getProductImage = useCallback(
    (
      key?: PartnerType | string | null,
      fallback: (type: PartnerType) => string = getPartnerImage,
    ): string => {
      const product = getProduct(key);
      return product?.image ?? fallback(key as PartnerType);
    },
    [getProduct],
  );

  const getProductColor = useCallback(
    (
      key?: PartnerType | string | null,
      fallback: (type: PartnerType) => string = getPartnerBgColor,
    ): string => {
      const product = getProduct(key);
      return product?.color ?? fallback(key as PartnerType);
    },
    [getProduct],
  );

  return {
    catalog,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    getProduct,
    getProductLabel,
    getProductImage,
    getProductColor,
  };
};
